import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { FileIcon, DefaultExtensionType } from "react-file-icon";
import { customFileIconStyles } from "../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import { Badge, Typography } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTrash, faEllipsis, faClose } from "@fortawesome/free-solid-svg-icons";

import { handleDownloadDocsFiles, handleDownloadFile } from "../../../services/documents/document-files-api.service";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

import { getFileExtension } from "../../../utils/files-utils/get-file-extension";
import { getItemPeriodValue } from "../../../models/documents/period";
import { getDocumentStatusIcon } from "../document-utils";
import { fileSize } from "../NewFile/utils";
import { intlFormatDateWithHour } from "../../../utils/time-utils";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";

import { Props, DocumentViewState } from "./interfaces";
import { Container, IconButton, FilesContainer, Actions, StyledFontAwesomeIcon } from "./styles";
import DocumentViewer from "../../../reusable-components/DocumentViewer/DocumentViewer";
import { READABLE_FILE_TYPES, READABLE_IMAGE_TYPES } from "../../../reusable-components/DocumentViewer/fileTypes";
import Popover from "@mui/material/Popover";

import ShareDocument from "../ShareDocument/ShareDocument";

const { UploadDocuments, DownloadDocuments } = AUTHORIZATIONS.LegalEntityAccess;
const { CompanyAccountsConfig } = AUTHORIZATIONS.SystemAccess;

const DocumentHeader = ({
  document,
  deleteDocument,
  customDocAndFilesNames,
  isGenericDocument,
  allowDownloadContent = true,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.document.actions"; // tRP - translation root path
  const tRPFiles = "components.upload_document.document_files_table"; // tRP = translation root path

  const allowUpload = useAuthorization([UploadDocuments]);
  const allowDownload = useAuthorization([DownloadDocuments]);
  const isSysAdmin = useAuthorization([CompanyAccountsConfig]);

  const [documentViewState, setDocumentViewState] = useState<DocumentViewState>({
    isViewing: false,
    fileDto: null,
  });

  return (
    <Container>
      <Typography
        title={`"${document?.name}" ${document?.legalEntity ? ` - ${document?.legalEntity?.name} - ` : ""} ${
          document?.type?.name ? document?.type.name : ""
        } ${document?.period ? getItemPeriodValue(document.period) : ""} ${
          document?.employee ? document.employee.name : ""
        }`}
      >
        {`${document?.type?.name ? document?.type.name : ""} ${
          document?.period ? getItemPeriodValue(document.period) : ""
        }`}
      </Typography>
      {document?.files && (
        <FilesContainer>
          {document.files.slice(0, 3).map((file) => {
            const fileExtension = getFileExtension(file.name).toLowerCase();
            return (
              <React.Fragment key={file.id}>
                {!READABLE_FILE_TYPES.includes(fileExtension) && !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                  <IconButton
                    title={`"${file.name}" ${fileSize(file.size)} ${t(`${tRPFiles}.fields.user`)} ${
                      file.createdByUser?.name ?? file.createdByUser?.email ?? ""
                    } ${intlFormatDateWithHour(file.createdAt)}`}
                    onClick={() => handleDownloadFile(file)}
                  >
                    <FileIcon
                      extension={fileExtension}
                      {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                      radius={3}
                    />
                  </IconButton>
                )}

                {(READABLE_FILE_TYPES.includes(fileExtension) || READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                  <IconButton
                    onClick={() => {
                      setDocumentViewState((documentViewState) => ({
                        ...documentViewState,
                        isViewing: true,
                        fileDto: file,
                      }));
                    }}
                  >
                    <FileIcon
                      extension={fileExtension}
                      {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                      radius={3}
                    />
                  </IconButton>
                )}
              </React.Fragment>
            );
          })}
          {document.files.length > 3 && (
            <Badge
              color={"primary"}
              showZero
              badgeContent={`+${document.files.length - 3}`}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              title={"More files..."}
            >
              <SimplePopover
                buttonType="icon"
                buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                flow="row"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
              >
                {document.files.slice(3).map((file) => {
                  const fileExtension = getFileExtension(file.name);

                  return (
                    <React.Fragment key={file.id}>
                      {!READABLE_FILE_TYPES.includes(fileExtension) &&
                        !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                          <IconButton
                            title={`"${file.name}" ${fileSize(file.size)} ${t(`${tRPFiles}.fields.user`)} ${
                              file.createdByUser?.name ?? file.createdByUser?.email ?? ""
                            } ${intlFormatDateWithHour(file.createdAt)}`}
                            onClick={() => handleDownloadFile(file)}
                          >
                            <FileIcon
                              extension={fileExtension}
                              {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                              radius={3}
                            />
                          </IconButton>
                        )}

                      {(READABLE_FILE_TYPES.includes(fileExtension) ||
                        READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                        <IconButton
                          onClick={() => {
                            setDocumentViewState((documentViewState) => ({
                              ...documentViewState,
                              isViewing: true,
                              fileDto: file,
                            }));
                          }}
                        >
                          <FileIcon
                            extension={fileExtension}
                            {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                            radius={3}
                          />
                        </IconButton>
                      )}
                    </React.Fragment>
                  );
                })}
              </SimplePopover>
            </Badge>
          )}
        </FilesContainer>
      )}

      {document?.status && <>{getDocumentStatusIcon(document.status)}</>}
      <Actions>
        <ShareDocument document={document} />
        {(allowDownload || isSysAdmin || allowDownloadContent) &&
          (document?.filesCount > 1 ? (
            <IconButton
              title={t(`${tRP}.download_document_files`)}
              onClick={() =>
                handleDownloadDocsFiles([document.id], customDocAndFilesNames, undefined, isGenericDocument)
              }
            >
              <FontAwesomeIcon icon={faDownload} />
            </IconButton>
          ) : (
            <IconButton
              title={t(`${tRP}.download_document_files`)}
              onClick={() => handleDownloadFile(document.files[0], undefined, undefined, isGenericDocument)}
            >
              <FontAwesomeIcon icon={faDownload} />
            </IconButton>
          ))}
        {(allowUpload || isSysAdmin) && (
          <IconButton
            title={t(`${tRP}.delete_document`)}
            onClick={() => deleteDocument(false)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </IconButton>
        )}
      </Actions>

      {!!documentViewState.fileDto && (
        <Popover
          open={documentViewState.isViewing}
          anchorPosition={{ left: 0, top: 0 }}
          anchorReference={"anchorPosition"}
        >
          <IconButton
            onClick={() => {
              setDocumentViewState({
                ...documentViewState,
                isViewing: false,
                fileDto: null,
              });
            }}
          >
            <StyledFontAwesomeIcon icon={faClose} />
          </IconButton>
          {documentViewState.fileDto && (
            <DocumentViewer
              fileDto={documentViewState.fileDto}
              isGenericDocument={!!isGenericDocument}
            />
          )}
        </Popover>
      )}
    </Container>
  );
};

export default DocumentHeader;
